
import React, { useEffect, useState } from "react";
import axios from "axios";
import Navbar from "../components/Navbar";
import {
  CalendarDays,
  Clock3,
  Phone,
  CircleCheckBig,
  CircleX,
  CircleDashed,
  Pill,
  Package,
  MapPin,
  Truck,
} from "lucide-react";
import {API_URL,FAST_API_URL} from "../api"


const PatientAllMedicineOrdersView = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const statusMap = {
    1: "pending",
    2: "confirmed",
    3: "packed",
    4: "out for delivery",
    5: "delivered",
    6: "cancelled",
  };

  const fetchOrders = async () => {
    try {
      const token = localStorage.getItem("token");

      const response = await axios.post(
        `${API_URL}/patient-all-chemist-bookings`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setOrders(response?.data?.orders || response?.data?.bookings || []);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  const getStatusUI = (status) => {
    switch (status) {
      case "confirmed":
      case "delivered":
        return {
          color: "bg-green-100 text-green-700 border-green-200",
          icon: <CircleCheckBig className="w-4 h-4" />,
        };

      case "cancelled":
        return {
          color: "bg-red-100 text-red-700 border-red-200",
          icon: <CircleX className="w-4 h-4" />,
        };

      case "pending":
        return {
          color: "bg-yellow-100 text-yellow-700 border-yellow-200",
          icon: <CircleDashed className="w-4 h-4" />,
        };

      default:
        return {
          color: "bg-blue-100 text-blue-700 border-blue-200",
          icon: <Truck className="w-4 h-4" />,
        };
    }
  };

  if (loading) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-cyan-50 pt-24 p-4">
          <div className="max-w-6xl mx-auto">
            <div className="animate-pulse grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
              {[...Array(6)].map((_, index) => (
                <div
                  key={index}
                  className="bg-white rounded-3xl h-80 shadow-sm"
                />
              ))}
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Navbar />

      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-cyan-50 pt-24 pb-10 px-4">
        <div className="max-w-7xl mx-auto mb-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-800">
                My Medicine Orders
              </h1>
              <p className="text-gray-500 mt-2">
                Track all your medicine orders easily
              </p>
            </div>

            <div className="bg-white px-5 py-3 rounded-2xl shadow-sm border border-indigo-100">
              <p className="text-sm text-gray-500">Total Orders</p>
              <h2 className="text-2xl font-bold text-indigo-600">
                {orders.length}
              </h2>
            </div>
          </div>
        </div>

        {orders.length === 0 ? (
          <div className="max-w-xl mx-auto bg-white rounded-3xl shadow-lg p-10 text-center">
            <div className="bg-indigo-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto">
              <Pill className="w-10 h-10 text-indigo-600" />
            </div>

            <h2 className="text-2xl font-bold text-gray-800 mt-5">
              No Medicine Orders Found
            </h2>

            <p className="text-gray-500 mt-2">
              You don’t have any medicine orders yet.
            </p>
          </div>
        ) : (
          <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {orders.map((item, index) => {
              const statusLabel =
                statusMap[Number(item?.order_status?.status_code)] || "pending";

              const statusUI = getStatusUI(statusLabel);

              return (
                <div
                  key={index}
                  className="relative bg-white/90 backdrop-blur-xl rounded-3xl shadow-lg border border-white overflow-hidden hover:shadow-2xl transition-all duration-300 hover:-translate-y-1"
                >
                  <div className="h-2 bg-gradient-to-r from-indigo-500 via-blue-500 to-cyan-400" />

                  <div className="p-6">
                    <div className="flex justify-between items-start mb-5">
                      <div>
                        <h2 className="text-lg font-bold text-gray-800">
                          {item?.chemist?.chemist_name}
                        </h2>

                        <p className="text-sm text-gray-500 mt-1">
                          Order ID: #{item?.order_id}
                        </p>
                      </div>

                      <div
                        className={`flex items-center gap-1 px-3 py-1.5 rounded-full border text-xs font-semibold capitalize ${statusUI.color}`}
                      >
                        {statusUI.icon}
                        {statusLabel}
                      </div>
                    </div>

                    <div className="space-y-4">
                      <div className="flex items-center gap-3">
                        <div className="bg-indigo-100 p-2 rounded-xl">
                          <Package className="w-5 h-5 text-indigo-600" />
                        </div>

                        <div>
                          <p className="text-xs text-gray-500">Chemist</p>
                          <p className="text-sm font-semibold text-gray-800">
                            {item?.chemist?.chemist_name}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-3">
                        <div className="bg-green-100 p-2 rounded-xl">
                          <Phone className="w-5 h-5 text-green-600" />
                        </div>

                        <div>
                          <p className="text-xs text-gray-500">Phone</p>
                          <p className="text-sm font-semibold text-gray-800">
                            {item?.chemist?.chemist_phone}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-3">
                        <div className="bg-purple-100 p-2 rounded-xl">
                          <CalendarDays className="w-5 h-5 text-purple-600" />
                        </div>

                        <div>
                          <p className="text-xs text-gray-500">Order Date</p>
                          <p className="text-sm font-semibold text-gray-800">
                            {item?.order_status?.event_date}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-3">
                        <div className="bg-orange-100 p-2 rounded-xl">
                          <Clock3 className="w-5 h-5 text-orange-600" />
                        </div>

                        <div>
                          <p className="text-xs text-gray-500">Order Time</p>
                          <p className="text-sm font-semibold text-gray-800">
                            {item?.order_status?.event_time}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-3">
                        <div className="bg-red-100 p-2 rounded-xl">
                          <MapPin className="w-5 h-5 text-red-600" />
                        </div>

                        <div>
                          <p className="text-xs text-gray-500">
                            Delivery Address
                          </p>
                          <p className="text-sm font-semibold text-gray-800">
                            {item?.delivery_address}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-3">
                        <div className="bg-green-100 p-2 rounded-xl">
                          <Package className="w-5 h-5 text-green-600" />
                        </div>

                        {/* <div>
                          <p className="text-xs text-gray-500">Total Amount</p>
                          <p className="text-sm font-bold text-green-600">
                            ₹{item?.total_amount}
                          </p>
                        </div> */}
                      </div>

                      <div className="flex items-center gap-3">
                        <div className="bg-blue-100 p-2 rounded-xl">
                          <Truck className="w-5 h-5 text-blue-600" />
                        </div>

                        <div>
                          <p className="text-xs text-gray-500">
                            Delivery Status
                          </p>
                          <p className="text-sm font-semibold text-gray-800">
                            {item?.delivery_status}
                          </p>
                        </div>
                      </div>

                      {item?.medicines?.length > 0 && (
                        <div>
                          <div className="flex items-center gap-2 mb-3">
                            <Pill className="w-5 h-5 text-indigo-600" />
                            <h3 className="font-semibold text-gray-800">
                              Ordered Medicines
                            </h3>
                          </div>

                          <div className="flex flex-wrap gap-2">
                            {item?.medicines?.map((medicine, idx) => (
  <div
    key={idx}
    className="px-3 py-3 rounded-xl bg-indigo-50 border border-indigo-200"
  >
    <p className="text-indigo-700 font-semibold">
      {medicine.name}
    </p>

    <div className="flex gap-2 mt-2">
      <span className="px-2 py-1 text-xs rounded-lg bg-blue-100 text-blue-700">
        Qty: {medicine.quantity}
      </span>

      <span className="px-2 py-1 text-xs rounded-lg bg-green-100 text-green-700">
        {medicine.unit}
      </span>
    </div>
  </div>
))}

                          </div>
                        </div>
                      )}
                    </div>

                    <div className="mt-6 pt-4 border-t border-gray-100 flex justify-between items-center">
                      <p className="text-xs text-gray-400">Created:</p>

                      <p className="text-xs font-medium text-gray-600">
                        {new Date(item?.created_at).toLocaleString()}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
};

export default PatientAllMedicineOrdersView;
